export const LEVEL_FADE_MS = 220

/** Fade from the current opacity so a reversed transition never jumps. */
export class VisibilityFade {
    alpha = 1
    visible = true
    private target?: number
    private from = 1
    private startedAt = 0

    reset(): void {
        this.target = undefined
        this.alpha = 1
        this.visible = true
    }

    update(show: boolean, now = Date.now()): void {
        const target = show ? 1 : 0
        if (this.target === undefined) {
            // A newly loaded, already hidden level should never flash on screen.
            this.alpha = target
            this.from = target
            this.target = target
            this.startedAt = now
        } else {
            if (this.target !== target) {
                this.from = this.alpha
                this.target = target
                this.startedAt = now
            }
            const progress = Math.max(0, Math.min(1, (now - this.startedAt) / LEVEL_FADE_MS))
            this.alpha = this.from + (target - this.from) * progress
        }
        this.visible = show || this.alpha > 0
    }
}

/** How long a storey's tone takes to settle after a change of level: the camera's floor pan (TranslationComputer.FLOOR_PAN_MS). */
export const STOREY_TONE_EASE_MS = 600

type Tone = readonly [number, number, number]
const NO_TONE: Tone = [1, 1, 1]

/**
 * A storey's tone (per-channel multipliers, scene_room_renderer.ts storeyTone) eased from where it stands toward where
 * it should be, as the camera pans to a new level, so climbing doesn't drop the storeys below a step darker in one frame.
 */
export class ToneEase {
    private current?: Tone
    private from: Tone = NO_TONE
    private target: Tone = NO_TONE
    private startedAt = 0

    /** Forget the tone: the next one is taken at once (a newly drawn storey). `from`: ease from it instead. */
    reset(from?: Tone): void {
        this.current = from
        this.from = this.target = from ?? NO_TONE
    }

    /** The tone to draw now on the way to `target` (undefined: none), or undefined once nothing is left to tone. */
    update(target: Tone | undefined, now = Date.now()): Tone | undefined {
        const goal = target ?? NO_TONE
        if (!this.current) {
            this.current = this.from = this.target = goal
            return target
        }
        if (goal.some((value, i) => value !== this.target[i])) {
            this.from = this.current
            this.target = goal
            this.startedAt = now
        }
        const progress = Math.max(0, Math.min(1, (now - this.startedAt) / STOREY_TONE_EASE_MS))
        const eased = progress * progress * (3 - 2 * progress)
        this.current = progress >= 1 ? goal
            : [0, 1, 2].map(i => this.from[i] + (goal[i] - this.from[i]) * eased) as unknown as Tone
        return this.current.every(value => value === 1) ? undefined : this.current
    }
}
